import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MapComponent from "../../components/MAPA-COMP/mapa-comp";
import freightService from "../../service/freight.service";
import driverService from "../../service/driver.service";

function toMs(raw) {
  if (!raw) return null;
  if (typeof raw === "number") return raw;
  if (raw.toDate) return raw.toDate().getTime();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

export default function PublicTrackPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [freightDoc, setFreightDoc] = useState(null);
  const [points, setPoints] = useState([]);
  const [driverName, setDriverName] = useState("");

  const freightId = freightDoc?.id;
  const publicTracking = freightDoc?.data?.publicTracking || {};

  const isExpired = useMemo(() => {
    const exp = toMs(publicTracking.expiresAt);
    if (!exp) return false;
    return Date.now() > exp;
  }, [publicTracking.expiresAt]);

  const isActive = publicTracking.active !== false;
  const canTrack = !!freightId && isActive && !isExpired;
  const isFreightFinished = useMemo(() => {
    if (!points.length) return false;
    return points[points.length - 1]?.concluded === true;
  }, [points]);

  useEffect(() => {
    let mounted = true;
    const fetchByToken = async () => {
      try {
        setLoading(true);
        setError("");
        const doc = await freightService.getFreightByPublicToken(token);
        if (!doc) {
          if (mounted) setError("Link de rastreio inválido.");
          return;
        }
        const freightData = doc.data();
        const uidDriver = freightData?.freight?.getDriverFreight?.uidDriver;
        if (uidDriver) {
          const driverSnap = await driverService.getDriverAvailable(uidDriver);
          if (driverSnap?.exists && mounted) {
            setDriverName(driverSnap.data()?.name || "");
          }
        }
        const stopSnap = await freightService.getStoppingPointsByFreight(doc.id);
        const stopPoints = stopSnap.docs.map((item) => {
          const d = item.data();
          return {
            coords: [d.latitude, d.longitude],
            popupText: `
              <strong>${d.name || "Parada"}</strong><br/>
              ${d.logradouro || ""}, ${d.number || ""}, ${d.city || ""} - ${d.uf || ""}
            `,
            operation: d.operation || [],
            concluded: !!d.concluded,
            locationTime:
              d.timestamp ?? d.createdAt ?? d.updatedAt ?? null,
          };
        });
        if (mounted) {
          setFreightDoc({ id: doc.id, data: freightData });
          setPoints(stopPoints);
        }
      } catch (e) {
        if (mounted) setError("Erro ao carregar rastreio.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchByToken();
    return () => {
      mounted = false;
    };
  }, [token]);

  const searchLatestPositions = async (lastPositionReceived) => {
    if (!freightId) return null;
    try {
      const snap = await freightService.searchLatestPositions(
        freightId,
        lastPositionReceived
      );
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      return null;
    }
  };

  return (
    <div style={{ padding: 20, background: "#f7f9fb", minHeight: "100vh" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0, color: "#012443" }}>Rastreio de frete</h2>
        {loading && <p>Carregando...</p>}
        {!loading && error && <p style={{ color: "#c62828" }}>{error}</p>}
        {!loading && !error && !isActive && (
          <p style={{ color: "#c62828" }}>Este link foi desativado.</p>
        )}
        {!loading && !error && isExpired && (
          <p style={{ color: "#c62828" }}>Este link expirou.</p>
        )}
        {!loading && !error && canTrack && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 10,
                background: "#ffffff",
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 1,
                    color: "#fff",
                    background: "#012443",
                    borderRadius: 999,
                    padding: "5px 10px",
                    lineHeight: 1,
                  }}
                >
                  FORTIO
                </div>
                <span style={{ color: "#334155", fontSize: 14 }}>
                  {driverName
                    ? `O motorista ${driverName} está na estrada realizando as entregas.`
                    : "Acompanhe em tempo real o progresso das entregas."}
                </span>
              </div>
            </div>
            <MapComponent
              points={points}
              idFreight={freightId}
              searchLatestPositions={searchLatestPositions}
              mapHeight={640}
              freightStatusDescribe={freightDoc?.data?.status?.describe}
            />
            {isFreightFinished && (
              <div
                style={{
                  marginTop: 12,
                  marginBottom: 4,
                  background: "#1f9d55",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                }}
              >
                Este frete já teve as entregas realizadas e foi finalizado.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
